import * as React from "react";
import * as SliderPrimitive from "@radix-ui/react-slider";
import { cn } from "@/utils/cn";
import { motion } from "framer-motion";
import { Info } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface AdvancedRangeSliderProps
  extends React.ComponentPropsWithoutRef<typeof SliderPrimitive.Root> {
  label?: string;
  description?: string;
  showInputs?: boolean;
  formatValue?: (value: number) => string;
  histogram?: number[];
  unit?: string;
  showTooltipValues?: boolean;
}

const AdvancedRangeSlider = React.forwardRef<
  React.ElementRef<typeof SliderPrimitive.Root>,
  AdvancedRangeSliderProps
>(
  (
    {
      className,
      label,
      description,
      showInputs = true,
      formatValue,
      histogram,
      unit,
      showTooltipValues = true,
      ...props
    },
    ref,
  ) => {
    const [localValue, setLocalValue] = React.useState<number[]>(
      props.value || props.defaultValue || [0, 100],
    );
    const [hoveredThumb, setHoveredThumb] = React.useState<number | null>(null);
    // Raw text the user is currently typing into the Min/Max inputs. A `null`
    // entry means "not editing", so the input falls back to the committed value.
    // Holding the in-progress text here lets users freely clear the field and
    // type intermediate values without each keystroke being parsed, clamped and
    // immediately snapped back — the behaviour that previously made these inputs
    // feel impossible to edit.
    const [inputDrafts, setInputDrafts] = React.useState<
      [string | null, string | null]
    >([null, null]);

    const handleValueChange = (newValue: number[]) => {
      setLocalValue(newValue);
      props.onValueChange?.(newValue);
    };

    const displayValue = props.value || localValue;

    // While typing, reflect the keystrokes verbatim without parsing or clamping.
    const handleInputChange = (index: number, value: string) => {
      if (props.disabled) return;
      setInputDrafts((prev) => {
        const next: [string | null, string | null] = [prev[0], prev[1]];
        next[index] = value;
        return next;
      });
    };

    // Parse, clamp and commit the typed value when the field loses focus (or the
    // user presses Enter). Empty or non-numeric input is discarded, reverting to
    // the last committed value.
    const commitInput = (index: number) => {
      const raw = inputDrafts[index];
      if (raw != null) {
        setInputDrafts((prev) => {
          const next: [string | null, string | null] = [prev[0], prev[1]];
          next[index] = null;
          return next;
        });
      }
      if (props.disabled || raw == null) return;

      const numValue = parseFloat(raw);
      if (isNaN(numValue)) return;

      const min = props.min ?? 0;
      const max = props.max ?? 100;
      const newValues = [...displayValue];
      newValues[index] = Math.max(min, Math.min(max, numValue));
      // Keep the pair ordered so the min thumb never crosses the max thumb.
      if (index === 0) {
        newValues[0] = Math.min(newValues[0] ?? min, newValues[1] ?? max);
      } else {
        newValues[1] = Math.max(newValues[0] ?? min, newValues[1] ?? max);
      }
      handleValueChange(newValues);
    };

    const handleInputKeyDown = (
      event: React.KeyboardEvent<HTMLInputElement>,
    ) => {
      if (event.key === "Enter") {
        event.currentTarget.blur();
      }
    };

    // Helper to determine decimal places from step value
    const getDecimalPlaces = (step: number): number => {
      const stepStr = step.toString();
      const decimalIndex = stepStr.indexOf(".");
      return decimalIndex === -1 ? 0 : stepStr.length - decimalIndex - 1;
    };

    const formatDisplay = (val: number) => {
      if (formatValue) return formatValue(val);

      // Format based on step precision
      const step = props.step ?? 1;
      const decimals = getDecimalPlaces(step);
      const formatted =
        decimals > 0 ? val.toFixed(decimals) : Math.round(val).toString();

      return unit ? `${formatted}${unit}` : formatted;
    };

    return (
      <div className="space-y-4">
        {label && (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-foreground">
                {label}
              </label>
              {description && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <Info className="w-3.5 h-3.5 text-muted-foreground" />
                    </TooltipTrigger>
                    <TooltipContent className="bg-dark-800 text-white p-3 rounded-lg max-w-xs border border-dark-700">
                      <p className="text-sm">{description}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
            <span className="text-sm font-mono text-muted-foreground">
              {formatDisplay(displayValue[0] ?? 0)} -{" "}
              {formatDisplay(displayValue[1] ?? 100)}
            </span>
          </div>
        )}

        <div className="relative">
          {/* Histogram visualization */}
          {histogram && (
            <div className="absolute inset-0 flex items-end h-8 -top-10 opacity-40">
              {histogram.map((value, i) => (
                <motion.div
                  key={i}
                  className="flex-1 bg-gradient-to-t from-brand-blue/40 to-brand-blue mx-px rounded-t"
                  initial={{ height: 0 }}
                  animate={{ height: `${value}%` }}
                  transition={{ delay: i * 0.02, duration: 0.3 }}
                />
              ))}
            </div>
          )}

          <SliderPrimitive.Root
            ref={ref}
            className={cn(
              "relative flex w-full touch-none select-none items-center py-4",
              className,
            )}
            value={displayValue}
            onValueChange={handleValueChange}
            {...props}
          >
            <SliderPrimitive.Track className="relative h-2 w-full grow overflow-hidden rounded-full bg-dark-800/80 backdrop-blur-sm shadow-inner">
              <SliderPrimitive.Range className="absolute h-full bg-gradient-to-r from-brand-blue to-brand-blue-dark shadow-sm" />
            </SliderPrimitive.Track>

            {displayValue.map((value, index) => (
              <TooltipProvider key={index}>
                <Tooltip open={showTooltipValues && hoveredThumb === index}>
                  <TooltipTrigger asChild>
                    <SliderPrimitive.Thumb
                      className={cn(
                        "block h-5 w-5 rounded-full bg-white shadow-lg",
                        "ring-offset-background transition-all duration-200",
                        "focus-visible:outline-none focus-visible:ring-2",
                        "focus-visible:ring-brand-blue focus-visible:ring-offset-2",
                        "disabled:pointer-events-none disabled:opacity-50",
                        "hover:scale-125 hover:shadow-glow",
                        "cursor-grab active:cursor-grabbing",
                        "border-2 border-brand-blue/20",
                      )}
                      onMouseEnter={() => setHoveredThumb(index)}
                      onMouseLeave={() => setHoveredThumb(null)}
                    >
                      <motion.div
                        className="absolute inset-0 rounded-full bg-gradient-to-r from-brand-blue to-brand-blue-dark"
                        initial={{ opacity: 0 }}
                        whileHover={{ opacity: 0.3 }}
                        whileTap={{ scale: 0.9 }}
                      />
                    </SliderPrimitive.Thumb>
                  </TooltipTrigger>
                  <TooltipContent
                    side="top"
                    className="bg-dark-800 text-white px-2 py-1 rounded text-xs border border-dark-700"
                  >
                    {formatDisplay(value)}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ))}
          </SliderPrimitive.Root>
        </div>

        {showInputs && (
          <div className="flex gap-4">
            <div className="flex-1">
              <div className="relative">
                <input
                  type="number"
                  value={inputDrafts[0] ?? displayValue[0]}
                  onChange={(e) => handleInputChange(0, e.target.value)}
                  onBlur={() => commitInput(0)}
                  onKeyDown={handleInputKeyDown}
                  disabled={props.disabled}
                  className={cn(
                    "w-full rounded-lg bg-dark-800/50 border border-dark-700",
                    "pl-3 pr-8 py-2 text-sm text-foreground",
                    // Hide the native number spinner — it eats width and clips the value (e.g. "200"
                    // -> "00" on focus). The slider + typing remain for adjustment.
                    "[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none",
                    "focus:outline-none focus:ring-2 focus:ring-brand-blue/50",
                    "focus:border-brand-blue transition-all duration-200",
                    "placeholder:text-muted-foreground",
                    "disabled:opacity-50 disabled:cursor-not-allowed",
                  )}
                  min={props.min}
                  max={props.max}
                  step={props.step}
                />
                {unit && (
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                    {unit}
                  </span>
                )}
              </div>
              <span className="text-xs text-muted-foreground mt-1 block text-center">
                Min
              </span>
            </div>
            <div className="flex items-center text-muted-foreground">
              <span className="text-sm">—</span>
            </div>
            <div className="flex-1">
              <div className="relative">
                <input
                  type="number"
                  value={inputDrafts[1] ?? displayValue[1]}
                  onChange={(e) => handleInputChange(1, e.target.value)}
                  onBlur={() => commitInput(1)}
                  onKeyDown={handleInputKeyDown}
                  disabled={props.disabled}
                  className={cn(
                    "w-full rounded-lg bg-dark-800/50 border border-dark-700",
                    "pl-3 pr-8 py-2 text-sm text-foreground",
                    // Hide the native number spinner — it eats width and clips the value (e.g. "200"
                    // -> "00" on focus). The slider + typing remain for adjustment.
                    "[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none",
                    "focus:outline-none focus:ring-2 focus:ring-brand-blue/50",
                    "focus:border-brand-blue transition-all duration-200",
                    "placeholder:text-muted-foreground",
                    "disabled:opacity-50 disabled:cursor-not-allowed",
                  )}
                  min={props.min}
                  max={props.max}
                  step={props.step}
                />
                {unit && (
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                    {unit}
                  </span>
                )}
              </div>
              <span className="text-xs text-muted-foreground mt-1 block text-center">
                Max
              </span>
            </div>
          </div>
        )}
      </div>
    );
  },
);

AdvancedRangeSlider.displayName = "AdvancedRangeSlider";

export { AdvancedRangeSlider };
